import { useState } from 'react';
import type { DirectoryPerson } from '../types';
import {
  affiliationRows,
  buildAffiliationRequest,
  canManageAffiliations,
  districtsOpenForManualAdd,
  otherDistrictIds,
  registrationInfo,
  showAffiliationsSection,
  type AffiliationRequest,
  type AffiliationRow,
} from '../lib/contactAffiliations';

/** The District Affiliations section of a Contact's detail panel.
 *
 *  Three things are shown, and labelled as three different things: where the my.Scouting import officially registers the person
 *  (read-only), the roster record's district, and the Compass-managed affiliations. Administrators can add, change and remove
 *  MANUAL affiliations; affiliations created by an Org Chart assignment are shown but are managed through that assignment.
 *  Who may SEE a person and their contact details is unchanged: it is decided by the directory, not by this component. */
export default function DistrictAffiliationsSection({
  person, districts, districtName, enabled, isAdmin, onMutate,
}: {
  person: DirectoryPerson;
  districts: Array<{ id: string; name: string }>;
  districtName: (id: string) => string;
  /** Whether this council has made District Affiliations available. */
  enabled: boolean;
  isAdmin: boolean;
  /** Sends one request for this Person. The caller supplies the network call; throws with a readable message on failure. */
  onMutate: (personId: string, request: AffiliationRequest, requestId: string) => Promise<void>;
}) {
  const [adding, setAdding] = useState(false);
  const [addIds, setAddIds] = useState<string[]>([]);
  const [addText, setAddText] = useState('');
  const [editing, setEditing] = useState<string | null>(null);
  const [editDistrict, setEditDistrict] = useState('');
  const [editText, setEditText] = useState('');
  const [requestId, setRequestId] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  if (!showAffiliationsSection(person, enabled)) return null;

  const manage = canManageAffiliations(person, { isAdmin }, enabled);
  const registration = registrationInfo(person, districtName);
  const rows = affiliationRows(person, districtName);
  const openDistricts = districtsOpenForManualAdd(person, districts);
  const other = otherDistrictIds(person);
  const newRequestId = () => `aff-${globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`}`;

  const send = async (request: AffiliationRequest, id: string, done: () => void) => {
    if (!person.personId) return;
    setBusy(true); setErr(null);
    try { await onMutate(person.personId, request, id); done(); }
    catch (e) { setErr(e instanceof Error ? e.message : 'Could not save.'); }
    finally { setBusy(false); }
  };

  const startAdd = () => { setAdding(true); setAddIds([]); setAddText(''); setErr(null); setRequestId(newRequestId()); };
  const submitAdd = () => {
    const built = buildAffiliationRequest({ operation: 'add', districtIds: addIds, description: addText });
    if ('error' in built) { setErr(built.error); return; }
    void send(built, requestId, () => setAdding(false));
  };
  const startEdit = (row: AffiliationRow) => { setEditing(row.key); setEditDistrict(row.districtId); setEditText(row.description ?? ''); setErr(null); setRequestId(newRequestId()); };
  const submitEdit = (row: AffiliationRow) => {
    const built = buildAffiliationRequest({ operation: 'update', row, districtId: editDistrict, description: editText });
    if ('error' in built) { setErr(built.error); return; }
    void send(built, requestId, () => setEditing(null));
  };
  const remove = (row: AffiliationRow) => {
    const built = buildAffiliationRequest({ operation: 'remove', row });
    if ('error' in built) { setErr(built.error); return; }
    if (!confirm(`Remove the ${row.districtName} affiliation for ${person.name}? Their registration and Org Chart positions are not affected.`)) return;
    void send(built, newRequestId(), () => undefined);
  };

  return (
    <section aria-label="District affiliations" style={{ marginTop: 18, paddingTop: 12, borderTop: '1px solid var(--line)' }}>
      <div style={{ fontWeight: 650 }}>Official registration</div>
      <div className="subtitle">Imported from my.Scouting. Compass never changes it.</div>
      <div style={{ padding: '4px 0 10px' }}>
        {registration.districtNames.length
          ? registration.districtNames.join(', ')
          : <span className="subtitle">No district in the my.Scouting import.</span>}
      </div>

      <div style={{ fontWeight: 650 }}>District affiliations</div>
      <div className="subtitle">
        Where this person serves, kept in Compass. They show who supports a district; they do not change registration, create a login or grant any access.
      </div>

      {person.contactDistrictId ? (
        <div style={{ padding: '6px 0' }}>
          <b>{districtName(person.contactDistrictId)}</b> <span className="subtitle">· Roster record</span>
        </div>
      ) : null}
      {rows.map((row) => (
        <div key={row.key} style={{ padding: '6px 0', borderTop: '1px solid var(--line)' }}>
          {editing === row.key ? (
            <div>
              <label>District</label>
              <select value={editDistrict} onChange={(e) => setEditDistrict(e.target.value)}>
                {districts
                  .filter((d) => d.id === row.districtId || openDistricts.some((o) => o.id === d.id))
                  .map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
              <label>What do they do for the district? (optional)</label>
              <textarea rows={2} value={editText} onChange={(e) => setEditText(e.target.value)} />
              <div style={{ display: 'flex', gap: 12, marginTop: 6 }}>
                <button className="btn primary" disabled={busy} onClick={() => submitEdit(row)}>Save</button>
                <button className="btn" disabled={busy} onClick={() => { setEditing(null); setErr(null); }}>Cancel</button>
              </div>
            </div>
          ) : (
            <>
              <div>
                <b>{row.districtName}</b> <span className="subtitle">· {row.sourceLabel}</span>
              </div>
              {row.description && <div className="subtitle">{row.description}</div>}
              {row.managedElsewhere && <div className="subtitle">Managed through the Org Chart assignment.</div>}
              {manage && !row.managedElsewhere && (
                <div style={{ display: 'flex', gap: 12, marginTop: 4 }}>
                  <button className="linkbtn" disabled={busy} onClick={() => startEdit(row)}>Edit</button>
                  <button className="linkbtn" disabled={busy} onClick={() => remove(row)}>Remove</button>
                </div>
              )}
            </>
          )}
        </div>
      ))}
      {other.length > 0 && (
        <div style={{ padding: '6px 0', borderTop: '1px solid var(--line)' }}>
          <b>{other.map(districtName).join(', ')}</b> <span className="subtitle">· Other relationship (earlier roster record or assignment)</span>
        </div>
      )}
      {rows.length === 0 && !person.contactDistrictId && other.length === 0 && (
        <div className="subtitle" style={{ padding: '6px 0' }}>No district affiliations.</div>
      )}

      {manage && !adding && openDistricts.length > 0 && (
        <button className="btn" style={{ marginTop: 8 }} onClick={startAdd}>Add affiliation</button>
      )}
      {manage && adding && (
        <div style={{ marginTop: 10 }}>
          <label>Districts this person supports</label>
          <div className="drawer-checklist">
            {openDistricts.map((d) => (
              <label key={d.id} className="checkbox-row">
                <input
                  type="checkbox"
                  checked={addIds.includes(d.id)}
                  onChange={() => setAddIds((cur) => (cur.includes(d.id) ? cur.filter((x) => x !== d.id) : [...cur, d.id]))}
                />
                {d.name}
              </label>
            ))}
          </div>
          <label>What do they do for the district? (optional)</label>
          <textarea rows={2} value={addText} placeholder="For example: serves on the finance committee" onChange={(e) => setAddText(e.target.value)} />
          <div style={{ display: 'flex', gap: 12, marginTop: 6 }}>
            <button className="btn primary" disabled={busy || addIds.length === 0} onClick={submitAdd}>Save</button>
            <button className="btn" disabled={busy} onClick={() => { setAdding(false); setErr(null); }}>Cancel</button>
          </div>
        </div>
      )}
      {err && <div className="err">{err}</div>}
    </section>
  );
}
